'use client'

import {makeObservable, observable, runInAction} from 'mobx'
import {observer, useLocalObservable} from 'mobx-react'
import {useEffect} from 'react'

import {z} from 'zod'

import * as UI from './date-form.styled'
import {useFormContext} from 'react-hook-form'
import {useQuery} from '@tanstack/react-query'
import {FormTestingRoutes, FormTestingViewModel} from '@/src/features/functional-testing/model/functional-testing-vm'
import {getInjection} from '@runlab/di'
import {AsyncProcessLoading} from '@/src/shared/ui/layout/ui/async-loading'
import type {FreeTimeSlot} from '@/src/features/functional-testing/core/dto'
import dayjs, {Dayjs} from 'dayjs'
import {useFormTesting} from '@/src/features/functional-testing'
import {FormTestingStep} from '@/src/features/functional-testing/store'
import {useFirstMountState} from 'react-use'
import {FormTemplate} from '@/src/shared/modules/form/ui/template'
import {Calendar} from '@/src/shared/ui/calendar'
import {toast} from 'react-toastify'

export type DateFormData = {
  lab_id?: string
  date?: Dayjs
  time?: FreeTimeSlot
  start_at?: string
}

export const DateFormSchema = z.object({
  date: z.any().optional(),
  time: z.any().optional(),
  start_at: z.string().min(1),
})

interface DateFormProps {
  routes: FormTestingRoutes
}

export const DateForm = observer(({routes}: DateFormProps) => {
  const firstMount = useFirstMountState()
  const testingFormStore = useFormTesting()

  const functionalTestingVm = useLocalObservable(() => {
    return new FormTestingViewModel(getInjection('FunctionalTestingRulesServiceInterface'), routes)
  })

  const form = useFormContext<DateFormData>()

  const labId = testingFormStore.shopId
  const date = form.watch('date')
  const time = form.watch('time')

  useEffect(() => {
    if (labId && !firstMount) {
      form.setValue('date', dayjs(new Date()))
      form.setValue('time', undefined)
      form.setValue('start_at', undefined)
    }
  }, [labId])

  useEffect(() => {
    testingFormStore.setDate(date)
    testingFormStore.setTime(time)

    if (labId && date && time) {
      testingFormStore.advance(FormTestingStep.Finish)
    }
  }, [labId, date, time])

  const times = useLocalObservable(() => ({
    items: [] as TimeObject[],
    get() {
      return this.items
    },
    set(freeTimeSlots: FreeTimeSlot[]) {
      this.items = freeTimeSlots.map(freeTimeSlot => new TimeObject(freeTimeSlot))
    },
  }))

  useEffect(() => {
    times.set(functionalTestingVm.freeTimeSlots)
  }, [functionalTestingVm.freeTimeSlots])

  // eslint-disable-next-line
  const weekRuleQueryState = useQuery({
    queryKey: ['lab-rules', labId],
    enabled: !!labId,
    queryFn: async () => {
      try {
        const rules = await functionalTestingVm.getWeekRules(labId!)

        functionalTestingVm.setWeekRules(rules.weekly)
        functionalTestingVm.setExceptions(rules.exceptions)

        const now = dayjs(new Date())

        if (functionalTestingVm.allowDay(now) && functionalTestingVm.allowDayOfWeek(now) && !date) {
          form.setValue('date', now)
        }
        return rules
      } catch (e: unknown) {
        if (e instanceof Error) {
          toast.error(e.message)
        }
        throw e
      }
    },
    staleTime: 0,
  })

  // eslint-disable-next-line
  const slotsQueryState = useQuery<FreeTimeSlot[]>({
    queryKey: ['lab-rules', labId, date],
    enabled: !!labId && !!date,
    queryFn: async () => {
      try {
        const slots = await functionalTestingVm.getTimeSlotsByLabOnDate(labId!, date!.format('YYYY-MM-DD'))

        runInAction(() => {
          functionalTestingVm.setTimeSlots(slots)
        })

        return slots
      } catch (e: unknown) {
        if (e instanceof Error) {
          toast.error(e.message)
        }
        throw e
      }
    },
    staleTime: 0,
  })

  const resolveDisabledDays = (current: any) => {
    if (!current) return false

    if (!functionalTestingVm.allowDay(current)) {
      return true
    }

    return !functionalTestingVm.allowDayOfWeek(current)
  }

  return (
    <>
      <FormTemplate title="Дата и время:">
        <UI.Wrapper>
          <UI.DateContainer>
            <Calendar
              disabledDate={resolveDisabledDays}
              value={date}
              onChange={selectedDate => {
                if (date && !selectedDate.isSame(date, 'day')) {
                  form.setValue('time', undefined)
                  form.setValue('start_at', undefined)
                }
                form.setValue('date', selectedDate)
              }}
            />
          </UI.DateContainer>
          <UI.TimeSelect>
            <UI.TimeTitle>Время:</UI.TimeTitle>

            {times.items.length > 0 ? (
              <UI.TimeItems>
                {times.items.map(time => (
                  <UI.TimeItem
                    key={time.slot.title}
                    $active={time.active}
                    onClick={() => {
                      times.items.map(iterTime => iterTime.disableActive())
                      time.toggleActive()
                      form.setValue('time', time.slot)
                      form.setValue('start_at', time.slot.start_at)
                    }}
                    aria-label="Select time">
                    {time.slot.title}
                  </UI.TimeItem>
                ))}
              </UI.TimeItems>
            ) : (
              <UI.TimeSlotsEmptyLabel>Нет доступного времени для записи</UI.TimeSlotsEmptyLabel>
            )}
          </UI.TimeSelect>
        </UI.Wrapper>
      </FormTemplate>

      {(weekRuleQueryState.isLoading ||
        weekRuleQueryState.isFetching ||
        slotsQueryState.isLoading ||
        slotsQueryState.isFetching) && <AsyncProcessLoading />}
    </>
  )
})

class TimeObject {
  @observable
  slot!: FreeTimeSlot

  @observable
  active = false

  constructor(slot: FreeTimeSlot) {
    runInAction(() => {
      this.slot = slot
    })

    makeObservable(this)
  }

  @observable
  disableActive() {
    this.active = false
  }

  @observable
  toggleActive() {
    this.active = !this.active
  }
}
